import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, User } from 'lucide-react';

interface BlogPostProps {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content?: string;
  date: string;
  author: string;
  image: string;
  isPreview?: boolean;
}

const BlogPost: React.FC<BlogPostProps> = ({
  title,
  slug,
  excerpt,
  content,
  date,
  author,
  image,
  isPreview = true,
}) => {
  const formattedDate = new Date(date).toLocaleDateString('nl-NL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const contentToShow = isPreview ? (
    <p className="text-gray-600 line-clamp-3">{excerpt}</p>
  ) : (
    <div className="prose prose-lg max-w-none prose-headings:mt-8 prose-headings:mb-4 prose-p:mb-4 prose-ul:my-4 prose-li:my-2">
      <div 
        className="[&>h2]:text-2xl [&>h2]:font-bold [&>h2]:text-gray-900 [&>h2]:mt-8 [&>h2]:mb-4
                 [&>p]:text-gray-700 [&>p]:leading-relaxed [&>p]:mb-4
                 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-4 [&>ul>li]:mb-2"
        dangerouslySetInnerHTML={{ __html: content || '' }} 
      />
    </div>
  );

  return (
    <article className="group h-full flex flex-col">
      <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden h-full flex flex-col border border-gray-100">
        {image && (
          <div className="h-48 overflow-hidden">
            <img
              src={image}
              alt={title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
        )}
        <div className="p-6 flex flex-col flex-grow">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-900 mb-2 leading-tight">
              {isPreview ? (
                <Link to={`/blog/${slug}`} className="hover:text-noordblue transition-colors">
                  {title}
                </Link>
              ) : (
                title
              )}
            </h2>
          </div>
          
          <div className="flex items-center text-sm text-gray-500 mb-4 space-x-4">
            <span className="flex items-center">
              <Calendar className="h-4 w-4 mr-1 text-noordblue" />
              {formattedDate}
            </span>
            <span className="flex items-center">
              <User className="h-4 w-4 mr-1 text-noordblue" />
              {author}
            </span>
          </div>
          
          <div className="mb-4 flex-grow">
            {contentToShow}
          </div>
          
          {isPreview && (
            <div className="mt-auto pt-4 border-t border-gray-100">
              <Link
                to={`/blog/${slug}`}
                className="text-noordblue hover:text-noordblue-dark font-medium inline-flex items-center group-hover:translate-x-1 transition-transform duration-200"
              >
                Lees verder
                <svg
                  className="w-4 h-4 ml-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </Link>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default BlogPost;
